'use client'

import { useState } from 'react'
import Image from 'next/image'
import { ADAPTIVE_TABS } from '@/data/adaptive-learning'
import { AdaptiveCopy } from './AdaptiveCopy'
import { cn } from '@/lib/utils'

/** "Why traditional studying fails" — vertical accordion with a swapping image pane on lg+. */
export function AdaptiveTabs() {
  const [active, setActive] = useState<number>(0)

  return (
    <section aria-labelledby="adaptive-tabs-heading" className="py-16 md:py-24">
      <div className="mx-auto max-w-5xl px-5 md:px-6">
        <h2
          id="adaptive-tabs-heading"
          className="font-heading text-ink text-center text-2xl font-semibold md:text-3xl"
        >
          Why traditional studying fails
        </h2>

        <div className="mt-10 lg:mt-14 lg:grid lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-12">
          <div role="group" aria-label="Why traditional studying fails">
            {ADAPTIVE_TABS.map((tab, i) => {
              const isActive = i === active
              const panelId = `adaptive-tab-panel-${tab.section.id}`
              return (
                <div key={tab.section.id} className="border-border border-b">
                  <h3 className="font-heading text-lg font-semibold md:text-xl">
                    <button
                      type="button"
                      aria-pressed={isActive}
                      aria-controls={panelId}
                      onClick={() => setActive(i)}
                      className={cn(
                        'flex min-h-11 w-full cursor-pointer items-stretch gap-3 py-4 text-left transition-colors',
                        isActive ? 'text-ink' : 'text-body hover:text-ink'
                      )}
                    >
                      <span
                        aria-hidden="true"
                        className={cn(
                          'w-1 shrink-0 self-stretch rounded-full',
                          isActive ? 'bg-primary' : 'bg-transparent'
                        )}
                      />
                      <span>{tab.section.heading}</span>
                    </button>
                  </h3>
                  <div
                    id={panelId}
                    hidden={!isActive}
                    className="space-y-3 pb-6 pl-4"
                  >
                    <AdaptiveCopy content={tab.section.contentBefore} />
                    <AdaptiveCopy content={tab.section.contentAfter} />
                    <div className="bg-primary-light mt-5 overflow-hidden rounded-2xl lg:hidden">
                      <Image
                        src={tab.image}
                        alt={tab.imageAlt}
                        width={1200}
                        height={896}
                        className="h-auto w-full p-6"
                      />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          <div aria-hidden="true" className="hidden lg:flex lg:items-center">
            <div className="bg-primary-light w-full rounded-3xl p-8">
              {ADAPTIVE_TABS.map((tab, i) => (
                <Image
                  key={tab.section.id}
                  src={tab.image}
                  alt={tab.imageAlt}
                  width={1200}
                  height={896}
                  hidden={i !== active}
                  className={cn(
                    'h-auto w-full',
                    i === active &&
                      'animate-[fadeIn_200ms_ease-out] motion-reduce:animate-none'
                  )}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
